import type { FC } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Box, Button, Container, Stack, SvgIcon, Typography } from '@mui/material';
import ArrowLeftIcon from '@untitled-ui/icons-react/build/esm/ArrowLeft';
import ArrowRightIcon from '@untitled-ui/icons-react/build/esm/ArrowRight';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { BlogCover } from 'src/components/blog-cover';
import { usePageView } from 'src/hooks/use-page-view';
import { getBlogPostBySlug } from 'src/data/blog-posts';
import { paths } from 'src/paths';
import ReactMarkdown from 'react-markdown';

const BlogPostPage: FC = () => {
  usePageView();
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPostBySlug(slug) : undefined;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const canonicalUrl = `https://mysticmediafilm.com/blog/${post.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.metaDescription,
    image: [`https://mysticmediafilm.com${post.image}`],
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Organization',
      name: 'Mystic Media Film',
      url: 'https://mysticmediafilm.com'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Mystic Media Film',
      logo: {
        '@type': 'ImageObject',
        url: 'https://mysticmediafilm.com/assets/logo-main.png'
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl
    },
    keywords: post.keywords,
    articleSection: post.category,
    inLanguage: 'en-US'
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://mysticmediafilm.com/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: 'https://mysticmediafilm.com/blog'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: post.title,
        item: canonicalUrl
      }
    ]
  };

  const faqSchema = post.faqs && post.faqs.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: post.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
          }
        }))
      }
    : null;

  return (
    <>
      <Seo
        title={post.metaTitle}
        description={post.metaDescription}
        keywords={post.keywords}
        canonicalUrl={canonicalUrl}
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}

      <main style={{ background: '#ffffff' }}>
        {/* Hero */}
        <Box sx={{ pt: { xs: '140px', md: '180px' }, pb: { xs: 6, md: 8 } }}>
          <Container maxWidth="md">
            <Stack spacing={4}>
              <Button
                component={RouterLink}
                href={paths.blog.index}
                startIcon={(
                  <SvgIcon sx={{ fontSize: 14 }}>
                    <ArrowLeftIcon />
                  </SvgIcon>
                )}
                sx={{
                  alignSelf: 'flex-start',
                  fontFamily: '"Inter", sans-serif',
                  color: '#52525b',
                  fontWeight: 500,
                  fontSize: 13,
                  letterSpacing: '-0.005em',
                  px: 1.5,
                  py: 0.75,
                  textTransform: 'none',
                  '&:hover': { color: '#0a0a0b', background: 'transparent' }
                }}
              >
                All posts
              </Button>

              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 999,
                    border: '1px solid #e4e4e7',
                    background: '#fafafa'
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Roboto Mono", monospace',
                      fontSize: 10,
                      fontWeight: 500,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      color: '#3f3f46'
                    }}
                  >
                    {post.category}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: '"Roboto Mono", monospace',
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: 0.8,
                    color: '#a1a1aa'
                  }}
                >
                  {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} · {post.readTime}
                </Typography>
              </Stack>

              <Typography
                component="h1"
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: '36px', sm: '52px', md: '64px' },
                  fontWeight: 700,
                  lineHeight: 1.05,
                  letterSpacing: '-0.035em',
                  color: '#0a0a0b'
                }}
              >
                {post.title}
              </Typography>

              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: 17, md: 20 },
                  fontWeight: 400,
                  lineHeight: 1.55,
                  color: '#52525b',
                  maxWidth: 720
                }}
              >
                {post.excerpt}
              </Typography>
            </Stack>
          </Container>
        </Box>

        {/* Featured cover */}
        <Container maxWidth="md" sx={{ pb: { xs: 6, md: 8 } }}>
          {post.image ? (
            <Box
              component="img"
              src={post.image}
              alt={post.title}
              sx={{
                width: '100%',
                borderRadius: 4,
                border: '1px solid #e4e4e7',
                boxShadow: '0 20px 40px -20px rgba(15, 23, 42, 0.18)',
                display: 'block'
              }}
            />
          ) : (
            <Box
              sx={{
                position: 'relative',
                aspectRatio: { xs: '16 / 10', md: '21 / 9' },
                borderRadius: 4,
                overflow: 'hidden',
                border: '1px solid #e4e4e7',
                boxShadow: '0 24px 48px -24px rgba(15, 23, 42, 0.28)'
              }}
            >
              <BlogCover category={post.category} size="lg" caption={post.readTime} />
            </Box>
          )}
        </Container>

        {/* Article body */}
        <Container maxWidth="md" sx={{ pb: { xs: 10, md: 14 } }}>
          <Box
            sx={{
              fontFamily: '"Inter", sans-serif',
              color: '#27272a',
              fontSize: { xs: 16, md: 17.5 },
              lineHeight: 1.75,
              '& h2': {
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: 26, md: 34 },
                fontWeight: 700,
                letterSpacing: '-0.03em',
                lineHeight: 1.15,
                color: '#0a0a0b',
                mt: { xs: 5, md: 7 },
                mb: 2.5
              },
              '& h3': {
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: 20, md: 24 },
                fontWeight: 600,
                letterSpacing: '-0.02em',
                color: '#0a0a0b',
                mt: 4,
                mb: 1.5
              },
              '& p': {
                mb: 2.5
              },
              '& ul, & ol': {
                pl: 3,
                mb: 2.5
              },
              '& li': {
                mb: 1,
                '&::marker': { color: '#2563eb' }
              },
              '& strong': {
                color: '#0a0a0b',
                fontWeight: 600
              },
              '& a': {
                color: '#2563eb',
                textDecoration: 'none',
                borderBottom: '1px solid rgba(37, 99, 235, 0.3)',
                transition: 'border-color 0.2s ease',
                '&:hover': { borderBottomColor: '#2563eb' }
              },
              '& code': {
                fontFamily: '"Roboto Mono", monospace',
                fontSize: '0.92em',
                background: '#f4f4f5',
                padding: '0.15em 0.4em',
                borderRadius: 1,
                color: '#0a0a0b'
              },
              '& blockquote': {
                borderLeft: '3px solid #2563eb',
                pl: 3,
                py: 0.5,
                my: 3,
                color: '#3f3f46',
                fontStyle: 'italic'
              }
            }}
          >
            <ReactMarkdown>{post.content}</ReactMarkdown>
          </Box>
        </Container>

        {/* Final CTA */}
        <Box sx={{ borderTop: '1px solid #f4f4f5', py: { xs: 14, md: 20 } }}>
          <Container maxWidth="lg">
            <Box
              sx={{
                position: 'relative',
                borderRadius: 4,
                overflow: 'hidden',
                background: '#0a0a0b',
                color: '#ffffff',
                px: { xs: 4, md: 8 },
                py: { xs: 7, md: 10 }
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '-30%',
                  right: '-10%',
                  width: 600,
                  height: 600,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(37, 99, 235, 0.4) 0%, transparent 65%)',
                  filter: 'blur(70px)',
                  pointerEvents: 'none'
                }}
              />
              <Stack spacing={5} alignItems="flex-start" sx={{ position: 'relative', zIndex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Box sx={{ width: 24, height: 1, background: 'rgba(255,255,255,0.5)' }} />
                  <Typography
                    sx={{
                      fontFamily: '"Roboto Mono", monospace',
                      fontSize: 11,
                      fontWeight: 500,
                      color: 'rgba(255,255,255,0.8)',
                      letterSpacing: 1.4,
                      textTransform: 'uppercase'
                    }}
                  >
                    Work with us
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: { xs: '36px', md: '60px' },
                    fontWeight: 700,
                    lineHeight: 1.04,
                    letterSpacing: '-0.04em',
                    color: '#ffffff',
                    maxWidth: 780
                  }}
                >
                  Got a project?{' '}
                  <Box component="span" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                    Let&apos;s talk.
                  </Box>
                </Typography>
                <Button
                  component={RouterLink}
                  href={paths.contact}
                  endIcon={(
                    <SvgIcon sx={{ fontSize: 16 }}>
                      <ArrowRightIcon />
                    </SvgIcon>
                  )}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    background: '#ffffff',
                    color: '#0a0a0b',
                    fontWeight: 500,
                    fontSize: 15,
                    letterSpacing: '-0.01em',
                    px: 2.75,
                    py: 1.5,
                    borderRadius: 999,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { background: '#f4f4f5' }
                  }}
                  variant="contained"
                >
                  Start a project
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>
      </main>
    </>
  );
};

export default BlogPostPage;
